import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { ChangePasswordData } from '@/models/LoginData';
import { ApplicationState } from '@/store';
import { getPasswordRulesRequest } from '@/store/appOrganization/action';
import { changePasswordClear, changePasswordFailure, changePasswordRequest } from '@/store/identity/action';
import { AxiosError } from 'axios';
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';

interface Props {
    handleModalClose?: () => void;
}

interface RuleValidationData {
    valid: boolean;
    key: string;
    used: boolean;
}

interface RuleValidationLabel {
    key: string;
    label: string;
    example?: string;
}

const ChangePassword = ({ handleModalClose }: Props) => {
    const passwordRulesConfigurationInitialState: RuleValidationData[] = [
        { valid: false, key: 'lowerAndCapitalLetter', used: true },
        { valid: false, key: 'number', used: true },
        { valid: false, key: 'minLength', used: true },
        { valid: false, key: 'specialCharacter', used: true },
    ];
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [visibilityPasswordType, setVisibilityPasswordType] = useState('password');
    const [visibilityPasswordIcon, setVisibilityPasswordIcon] = useState(Constants.icons.VISIBILITY_OFF);
    const [showMessage, setShowMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [hasChecklistError, setHasChecklistError] = useState(false);
    const [passwordRulesValidation, setPasswordRulesValidation] = useState<RuleValidationData[]>(passwordRulesConfigurationInitialState);
    const [passwordRulesConfiguration, setPasswordRulesConfiguration] = useState<RuleValidationLabel[]>([]);

    const [isNewPassFocused, setIsNewPassFocused] = useState(false);

    const { passwordRules } = useSelector((state: ApplicationState) => state.appOrganization.data);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: identity, success, hasError, error } = useSelector((state: ApplicationState) => state.identity);

    const dispatch = useDispatch();

    const submitTextButton = useMemo(() => {
        return identity.user.mustChangePassword ? strings.CHANGE_AND_CONTINUE : strings.CONFIRM_CHANGES;
    }, [identity, strings]);

    useEffect(() => {
        dispatch(getPasswordRulesRequest(identity.person.orgId));
        loadPasswordChecklist();
    }, []);

    useEffect(() => {
        loadPasswordChecklist();
    }, [passwordRules]);

    useEffect(() => {
        setHasChecklistError(passwordRulesValidation.findIndex((passRule: RuleValidationData) => passRule.used && !passRule.valid) > -1);
    }, [passwordRulesValidation, passwordRules, passwordRulesConfiguration]);

    useEffect(() => {
        if (hasError) {
            setShowErrorMessage(true);
            setShowMessage(false);
        } else {
            setShowErrorMessage(false);
        }
    }, [hasError]);

    useEffect(() => {
        if (success) {
            setShowMessage(true);
            setShowErrorMessage(false);
            setCurrentPassword('');
            setNewPassword('');
            setRepeatPassword('');
            setPasswordRulesValidation(passwordRulesConfigurationInitialState);
            closeModal();
        } else {
            setShowMessage(false);
        }
    }, [dispatch, success]);

    useEffect(() => {
        dispatch(changePasswordClear());
    }, [dispatch]);

    const loadPasswordChecklist = () => {
        if (passwordRules && passwordRulesConfiguration.length === 0) {
            for (const [key, value] of Object.entries(passwordRules)) {
                switch (key) {
                    case 'lowerAndCapitalLetter':
                        value && passwordRulesConfiguration.push({ key, label: strings.PASSWORD_CHECKLIST_UPPERLOWERCASE });
                        break;
                    case 'minLength':
                        value > 0 &&
                            passwordRulesConfiguration.push({ key, label: strings.PASSWORD_CHECKLIST_CHARACTERS.replace('%n', value.toString()) });
                        break;
                    case 'number':
                        value && passwordRulesConfiguration.push({ key, label: strings.PASSWORD_CHECKLIST_HAS_NUMBERS });
                        break;
                    case 'specialCharacter':
                        value &&
                            passwordRulesConfiguration.push({
                                key,
                                label: strings.PASSWORD_CHECKLIST_SPECIAL_CHARACTERS,
                                example: strings.PASSWORD_CHECKLIST_SPECIAL_CHAR_EXAMPLES,
                            });
                        break;
                    default:
                }
            }
            setPasswordRulesConfiguration(Array.from(passwordRulesConfiguration));
        }
    };

    const validatePasswordRules = (pass: string) => {
        if (passwordRules) {
            const enoughChars = new RegExp(`^.{${passwordRules.minLength},}$`);
            const oneLowerAndOneUppercase: RegExp = new RegExp('^(?=.*[a-z])(?=.*[A-Z])');
            const oneNumericDigit: RegExp = new RegExp('^(?=.*[0-9])');
            const oneSpecialChar = new RegExp('\\W');
            setPasswordRulesValidation([
                {
                    key: 'minLength',
                    used: passwordRules.minLength !== undefined && passwordRules.minLength > 0,
                    valid: passwordRules.minLength > 0 && enoughChars.test(pass) ? true : false,
                },
                {
                    key: 'lowerAndCapitalLetter',
                    used: passwordRules.lowerAndCapitalLetter,
                    valid: passwordRules.lowerAndCapitalLetter && oneLowerAndOneUppercase.test(pass) ? true : false,
                },
                {
                    key: 'specialCharacter',
                    used: passwordRules.specialCharacter,
                    valid: passwordRules.specialCharacter && oneSpecialChar.test(pass) ? true : false,
                },
                { key: 'number', used: passwordRules.number, valid: passwordRules.number && oneNumericDigit.test(pass) ? true : false },
            ]);
        }
    };

    const handleVisibityChange = (event: SyntheticEvent): void => {
        event.preventDefault();
        const icons: string[] = [Constants.icons.VISIBILITY_OFF, Constants.icons.VISIBILITY];
        const types: string[] = ['password', 'text'];

        setVisibilityPasswordType(types[+(visibilityPasswordType === types[0])]);
        setVisibilityPasswordIcon(icons[+(visibilityPasswordIcon === icons[0])]);
    };

    const handleSubmit = () => {
        if (newPassword === repeatPassword) {
            const payload: ChangePasswordData = {
                actualPassword: currentPassword,
                applicationId: identity.person.applicationId,
                newPassword,
                userId: identity.user.id,
            };
            dispatch(changePasswordRequest(payload));
        } else {
            dispatch(changePasswordFailure({ response: { status: Constants.statusCode.UNAUTHORIZED } } as AxiosError));
        }
    };

    const closeModal = () => {
        const htmlElement: HTMLElement | null = document.getElementById('change-modal');
        htmlElement && htmlElement.click();
        handleModalClose && handleModalClose();
    };

    const isPasswordCharsDifferentSoFar = () => {
        if (newPassword.length > 0 && repeatPassword.length > 0) {
            if (!newPassword.startsWith(repeatPassword)) {
                return true;
            } else if (isNewPassFocused && newPassword.length > repeatPassword.length) {
                return true;
            }
        }
        return false;
    };

    const displayResultLayout = (successString: string, failureString: string) => {
        const isRepeatPasswordDifferent = isPasswordCharsDifferentSoFar();

        if (!isRepeatPasswordDifferent && showMessage && !showErrorMessage) {
            return successString;
        } else if (isRepeatPasswordDifferent || showErrorMessage) {
            return failureString;
        } else return '';
    };

    const displayResultMessage = () => {
        const isRepeatPasswordDifferent = isPasswordCharsDifferentSoFar();

        if (isRepeatPasswordDifferent) {
            return strings.PASSWORD_AND_CONFIRMATION_NOT_EQUAL;
        } else if (!isRepeatPasswordDifferent && showMessage && !showErrorMessage) {
            return strings.CHANGE_PASS_SUCCESS;
        } else if (showErrorMessage) {
            return getErrorMessage();
        } else {
            return '';
        }
    };

    const getErrorMessage = (): string => {
        if (error && error.response) {
            switch (error.response.status) {
                case Constants.statusCode.PRECONDITION_FAILED:
                    return strings.INCORRECT_CURRENT_PASSWORD;
                case Constants.statusCode.UNAUTHORIZED:
                    return strings.PASSWORD_AND_CONFIRMATION_NOT_EQUAL;
                case Constants.statusCode.INVALID_PASSWORD_FORMAT_STATUS:
                    return strings.INVALID_PASSWORD_FORMAT;
                default:
                    return strings.CHANGE_PASS_FAILURE;
            }
        }
        return strings.CHANGE_PASS_FAILURE;
    };

    const renderPasswordChecklist = (label: string, example: string = '', isValidated: boolean) => {
        return (
            <div className="checklist-item" key={label}>
                <div className="checked-icon-col">
                    {isValidated && <i className="material-icons align-middle checked-icon">{Constants.icons.CHECK_CIRCLE}_outline</i>}
                    {!isValidated && <span>-</span>}
                </div>
                <div className={isValidated ? 'checked-rule' : 'not-checked-rule'}>
                    {label} {!!example && <span>({example})</span>}
                </div>
            </div>
        );
    };

    return (
        <>
            {!passwordRules && <Loader />}
            {passwordRules && (
                <>
                    <form autoComplete="off" className="flex-column" id="changePasswordForm">
                        <div className="row input-section">
                            <div className="title-container">
                                <h1>{strings.CURRENT_PASS}</h1>
                            </div>
                            <input
                                autoComplete="nope"
                                className="form-control"
                                disabled={success}
                                name="currentPassword"
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder={strings.CURRENT_PASS}
                                required
                                type={visibilityPasswordType}
                                value={currentPassword || ''}
                            />
                        </div>
                        <div className="row input-section">
                            <div className="title-container">
                                <h1>{strings.NEW_PASS}</h1>
                            </div>
                            <input
                                autoComplete="nope"
                                className="form-control"
                                disabled={success}
                                name="newPassword"
                                onBlur={() => setIsNewPassFocused(false)}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    validatePasswordRules(e.target.value);
                                }}
                                onFocus={() => setIsNewPassFocused(true)}
                                placeholder={strings.NEW_PASS}
                                required
                                type={visibilityPasswordType}
                                value={newPassword || ''}
                            />
                        </div>
                        <div className="row input-section">
                            <div className="title-container">
                                <h1>{strings.REPEAT_PASS}</h1>
                            </div>
                            <div className="inner-addon right-addon">
                                <button className="btn btn-link" onClick={handleVisibityChange} type="button">
                                    <i className="material-icons">{visibilityPasswordIcon}</i>
                                </button>
                                <input
                                    autoComplete="nope"
                                    className="form-control"
                                    disabled={success}
                                    name="repeatNewPassword"
                                    onChange={(e) => setRepeatPassword(e.target.value)}
                                    placeholder={strings.REPEAT_PASS}
                                    type={visibilityPasswordType}
                                    value={repeatPassword}
                                />
                            </div>
                        </div>
                        <div className="checklist-container">
                            <p className="checklist-title">{strings.PASSWORD_CHECKLIST_TITLE}</p>
                            <div className="checklist-table">
                                {passwordRulesConfiguration &&
                                    passwordRulesConfiguration.length > 0 &&
                                    passwordRulesConfiguration.map((rule: RuleValidationLabel) => {
                                        return renderPasswordChecklist(
                                            rule.label,
                                            rule.example,
                                            passwordRulesValidation[passwordRulesValidation.findIndex((item) => item.key === rule.key)].valid,
                                        );
                                    })}
                            </div>
                        </div>

                        <div className={`message ${displayResultLayout(Constants.SUCCESS, Constants.ERROR_STATUS)}`}>
                            <div className="success-msg-icon-container">
                                <i className="material-icons align-middle">
                                    {displayResultLayout(Constants.icons.CHECK_CIRCLE, Constants.icons.ALERT_ERROR)}
                                </i>
                            </div>
                            <div className="success-msg-container">
                                <span>{displayResultMessage()} </span>
                            </div>
                        </div>
                        <div className="modal-confirm-button">
                            <button
                                className="btn btn-secondary confirm-button"
                                disabled={
                                    !showMessage &&
                                    (hasChecklistError ||
                                        currentPassword.length === 0 ||
                                        newPassword.length === 0 ||
                                        repeatPassword.length === 0 ||
                                        newPassword != repeatPassword)
                                }
                                onClick={!showMessage ? handleSubmit : closeModal}
                                type="button"
                            >
                                {!showMessage ? strings.CONFIRM_CHANGES : strings.UNDERSTAND}
                            </button>
                        </div>
                    </form>
                </>
            )}
        </>
    );
};

export default ChangePassword;
